import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '@ikea-customer-self-serve/gatsby-plugin-translations';
import { colors } from '../styles';
import { useStateSelector } from '../store';
import { DisabledPrevButtonType } from '../reducers/app/appTypes';

export type Page = 'start' | 'services' | 'timeslots' | 'contact';

type ProgressProps = {
  page: Page;
};

interface StepProps {
  $isAfterActiveStep: boolean;
}

interface DotProps {
  $isAfterActiveStep: boolean;
  $isActiveStep: boolean;
}

const NUMBER_OF_STEPS = 4;
const getSteps = (totalSteps: number) =>
  Array.from(Array(totalSteps).keys()).map((count) => count + 1);

const ProgressBar = styled.div<{ $nbrOfSteps: number }>`
  display: flex;
  margin: 0 auto;
  height: 2.5rem;
  max-width: 100%;
  width: ${({ $nbrOfSteps }): string => `${$nbrOfSteps * 1.6}rem`};
`;

const Step = styled.div<StepProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 0%;

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: -50%;
    height: 0.1rem;
    width: 100%;
    background-color: ${({ $isAfterActiveStep }): string =>
      $isAfterActiveStep
        ? colors.background.alternative
        : colors.background.accent};
    transform-origin: center;
    transform: translate(0, -50%);
    z-index: 1;
  }
  &:first-child::before {
    content: none;
  }
`;

const Dot = styled.div<DotProps>`
  width: ${({ $isActiveStep }): string =>
    $isActiveStep ? '0.75rem' : '0.5rem'};
  height: ${({ $isActiveStep }): string =>
    $isActiveStep ? '0.75rem' : '0.5rem'};
  border: 1px solid ${colors.neutral.white};
  border-radius: 50%;
  background-color: ${({ $isAfterActiveStep }): string =>
    $isAfterActiveStep
      ? colors.background.alternative
      : colors.background.accent};
  position: relative;
  z-index: 2;
`;

export const getActiveStep = (
  disabledPrevButton: DisabledPrevButtonType,
  page: Page,
): number => {
  if (page === 'services') {
    return disabledPrevButton === 'services' ? 1 : 2;
  }
  if (page === 'timeslots') {
    return disabledPrevButton === 'timeslots'
      ? 1
      : disabledPrevButton === 'services'
        ? 2
        : 3;
  }
  if (page === 'contact') {
    return disabledPrevButton === 'timeslots'
      ? 2
      : disabledPrevButton === 'services'
        ? 3
        : 4;
  }
  return 1;
};

const getTotalSteps = (disabledPrevButton: DisabledPrevButtonType): number => {
  switch (disabledPrevButton) {
    case 'services':
      return 3;
    case 'timeslots':
      return 2;
    default:
      return 4;
  }
};

const Progress: React.FC<ProgressProps> = ({ page }) => {
  const { t } = useTranslation();
  const disabledPrevButton = useStateSelector(
    (state) => state.app.disabledPrevButton,
  );

  const activeStep = getActiveStep(disabledPrevButton, page);
  const totalSteps = getTotalSteps(disabledPrevButton);

  return (
    <ProgressBar
      $nbrOfSteps={totalSteps || NUMBER_OF_STEPS}
      title={t('components.appHeader.stepsProgress', {
        activeStep,
        numberOfSteps: totalSteps,
      })}
      aria-label={t('components.appHeader.stepsProgress', {
        activeStep,
        numberOfSteps: totalSteps,
      })}
      role="progressbar"
    >
      {getSteps(totalSteps || NUMBER_OF_STEPS).map(
        (step): JSX.Element => (
          <Step key={step.toString()} $isAfterActiveStep={step > activeStep}>
            <Dot
              $isActiveStep={step === activeStep}
              $isAfterActiveStep={step > activeStep}
            />
          </Step>
        ),
      )}
    </ProgressBar>
  );
};

export default Progress;
